(() => {
  if (!window.customElements || customElements.get('divano-hero-video')) return;

  class DivanoHeroVideo extends HTMLElement {
    connectedCallback() {
      if (this.started) {
        if (!this.impressed) this.impressionObserver?.observe(this.section);
        if (!this.videoError && !this.video?.hasAttribute('src')) this.sourceObserver?.observe(this.section);
        if (!this.videoError) this.videoObserver?.observe(this.section);
        if (!this.videoObserver && this.video?.hasAttribute('src') && !this.userPaused && this.video.paused) {
          this.playVideo();
        }
        return;
      }
      if (this.pending) return;
      this.section = this.closest('.divano-hero-band');
      if (!this.section) return;
      this.pending = true;
      this.afterLoad = () => {
        this.startTimer = setTimeout(() => {
          this.pending = false;
          if (this.isConnected) this.start();
        }, 0);
      };
      if (document.readyState === 'complete') this.afterLoad();
      else window.addEventListener('load', this.afterLoad, { once: true });
    }

    disconnectedCallback() {
      if (this.pending) {
        window.removeEventListener('load', this.afterLoad);
        clearTimeout(this.startTimer);
        this.pending = false;
      }
      this.impressionObserver?.disconnect();
      this.sourceObserver?.disconnect();
      this.videoObserver?.disconnect();
      if (this.video && !this.video.paused) {
        this.frameRequest++;
        this.video.pause();
        this.video.classList.remove('is-playing');
        if (this.toggle) this.setToggleState('paused');
      }
    }

    publish(name) {
      try { window.Shopify?.analytics?.publish?.(name, this.payload); } catch (e) {  }
    }

    start() {
      if (this.started) return;
      this.started = true;
      const section = this.section;
      this.device = matchMedia('(max-width: 749px)').matches ? 'mobile' : 'desktop';
      const videoAsset = this.device === 'mobile' ? section.dataset.heroVideoMobile : section.dataset.heroVideo;
      const imageAsset = this.device === 'mobile' && section.dataset.heroImageMobile
        ? section.dataset.heroImageMobile : section.dataset.heroImage;
      this.payload = {
        campaign: section.dataset.heroCampaign || '',
        asset: videoAsset || imageAsset || '',
        device: this.device
      };
      this.cta = section.querySelector('.divano-hero-band__cta');
      this.onClick = () => this.publish('divano_hero_click');
      this.cta?.addEventListener('click', this.onClick);

      if ('IntersectionObserver' in window) {
        this.impressionObserver = new IntersectionObserver((entries) => {
          if (!this.isConnected) return;
          if (!entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.5)) return;
          this.impressionObserver.disconnect();
          if (!this.impressed) {
            this.impressed = true;
            this.publish('divano_hero_impression');
          }
        }, { threshold: 0.5 });
        this.impressionObserver.observe(section);
      }

      this.video = section.querySelector('.divano-hero-band__video');
      this.toggle = section.querySelector('.divano-hero-band__video-toggle');
      const src = this.video?.dataset[`src${this.device === 'mobile' ? 'Mobile' : 'Desktop'}`];
      const connection = navigator.connection;
      if (!this.video || !this.toggle || !src ||
          matchMedia('(prefers-reduced-motion: reduce)').matches ||
          connection?.saveData === true ||
          connection?.effectiveType === 'slow-2g' || connection?.effectiveType === '2g') return;

      this.userPaused = false;
      this.playPublished = false;
      this.completePublished = false;
      this.lastTime = null;
      this.frameRequest = 0;
      this.onPlaying = () => {
        if (!this.isConnected || this.videoError || this.video.paused) return;
        if (this.userPaused || this.withinVideoMargin === false) {
          this.video.pause();
          this.setToggleState('paused');
          return;
        }
        this.toggle.hidden = false;
        this.setToggleState('playing');
        if (!this.playPublished) {
          this.playPublished = true;
          this.publish('divano_hero_video_play');
        }
        const request = ++this.frameRequest;
        const reveal = () => {
          if (this.isConnected && !this.videoError && !this.video.paused && request === this.frameRequest) {
            this.video.classList.add('is-playing');
          }
        };
        if (typeof this.video.requestVideoFrameCallback === 'function') this.video.requestVideoFrameCallback(reveal);
        else reveal();
      };
      this.onTimeUpdate = () => {
        const current = this.video.currentTime;
        if (!this.completePublished && this.lastTime !== null && current < this.lastTime) {
          this.completePublished = true;
          this.publish('divano_hero_video_complete');
        }
        this.lastTime = current;
      };
      this.onError = () => {
        this.videoError = true;
        this.video.pause();
        this.video.classList.remove('is-playing');
        this.toggle.hidden = true;
        this.video.removeAttribute('src');
        this.videoObserver?.disconnect();
      };
      this.onToggle = () => {
        if (this.video.paused) {
          this.userPaused = false;
          this.playVideo();
        } else {
          this.userPaused = true;
          this.video.pause();
          if (this.video.readyState >= 2) this.video.classList.add('is-playing');
          this.setToggleState('paused');
        }
      };
      this.video.addEventListener('playing', this.onPlaying);
      this.video.addEventListener('timeupdate', this.onTimeUpdate);
      this.video.addEventListener('error', this.onError);
      this.toggle.addEventListener('click', this.onToggle);

      if ('IntersectionObserver' in window) {
        this.videoObserver = new IntersectionObserver((entries) => {
          if (!this.isConnected || this.videoError || !this.video.hasAttribute('src')) return;
          const visible = entries.some((entry) => entry.isIntersecting);
          this.withinVideoMargin = visible;
          if (!visible) {
            if (!this.video.paused) {
              this.video.pause();
              this.setToggleState('paused');
            }
            if (!this.userPaused) {
              this.frameRequest++;
              this.video.classList.remove('is-playing');
            }
          } else if (!this.userPaused && this.video.paused) {
            this.playVideo();
          }
        }, { rootMargin: `${Math.max(1, Math.round(window.innerHeight))}px 0px` });
        this.sourceObserver = new IntersectionObserver((entries) => {
          if (!this.isConnected || this.videoError || this.video.hasAttribute('src') ||
              !entries.some((entry) => entry.isIntersecting)) return;
          this.video.muted = true;
          this.video.src = src;
          this.sourceObserver.disconnect();
          this.videoObserver.observe(section);
          this.playVideo();
        });
        this.sourceObserver.observe(section);
      } else {
        this.video.muted = true;
        this.video.src = src;
        this.playVideo();
      }
    }

    setToggleState(state) {
      this.toggle.dataset.state = state;
      this.toggle.setAttribute('aria-label', state === 'playing' ? 'Pause background video' : 'Play background video');
    }

    playVideo() {
      if (!this.isConnected || this.videoError) return;
      try {
        const promise = this.video.play();
        promise?.catch(() => {});
      } catch (e) {  }
    }
  }

  customElements.define('divano-hero-video', DivanoHeroVideo);
})();
